import { LandingLayoutWithLandingDescription } from "PFApp/components/landing_layout";
import { Button } from "PFComponents/button";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useCurrentAccount } from "PFCore/hooks/queries/account/use_current_account";
import { useRequestAccess } from "PFCore/hooks/queries/auth/use_request_access";
import { FeatureFlag } from "PFTypes";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router";

import { ErrorsAlert } from "./parts/errors_alert";
import css from "./request_access_page.module.scss";

export const RequestAccessPage = () => {
  const { t } = useTranslation();
  const { getSingleErrorByType } = useResponseErrors();
  const [data, setData] = useState({ name: "", company: "", email: "" });

  const { data: currentAccount } = useCurrentAccount();
  const isRequestAccessEnabled = useIsFeatureEnabled(FeatureFlag.RequestAccess);

  const { mutate: requestAccess, isPending, isSuccess, error, isError } = useRequestAccess();

  const errors = useMemo(
    () => (isError ? getSingleErrorByType(error) : {}),
    [isError, error, getSingleErrorByType]
  );

  if (!isRequestAccessEnabled || !currentAccount) {
    return <Navigate to="/" replace />;
  }

  const handleSubmitClick = () => {
    requestAccess({
      ...data,
      accountId: currentAccount.id
    });
  };

  const showErrorAlert = errors.name || errors.company || errors.email;

  return (
    <LandingLayoutWithLandingDescription>
      <div role="form" aria-label={t("signUpSignIn.requestAccess")} data-qa-id="RequestAccessPage.form">
        <strong data-qa-id="RequestAccessPage.header">{t("signUpSignIn.requestAccess")}</strong>
        {isSuccess && <div style={{ margin: "30px 0" }}>{t("signUpSignIn.accessRequestReceived")}</div>}
        {showErrorAlert && (
          <ErrorsAlert
            qaId="RequestAccessPage.error"
            className={css.errorsBox}
            errors={[
              {
                key: "fullName",
                label: t("signUpSignIn.fullName"),
                error: errors.name
              },
              {
                key: "company",
                label: t("signUpSignIn.company"),
                error: errors.company
              },
              {
                key: "email",
                label: t("signUpSignIn.companyEmail"),
                error: errors.email
              }
            ]}
          />
        )}

        <InputFieldSet
          qaId="RequestAccessPage.name"
          label={t("signUpSignIn.fullName")}
          style={{ display: "block", marginBottom: 20 }}
          onChange={(name) => setData((data) => ({ ...data, name }))}
        />

        <InputFieldSet
          qaId="RequestAccessPage.company"
          label={t("signUpSignIn.company")}
          style={{ display: "block", marginBottom: 20 }}
          onChange={(company) => setData((data) => ({ ...data, company }))}
        />

        <InputFieldSet
          qaId="RequestAccessPage.email"
          inputType="email"
          label={t("signUpSignIn.companyEmail")}
          style={{ display: "block", marginBottom: 30 }}
          onChange={(email) => setData((data) => ({ ...data, email }))}
        />

        <Button
          kind="primary"
          style={{ width: "100%" }}
          onClick={handleSubmitClick}
          qaId="RequestAccessPage.signUpButton"
          disabled={data.name.trim() === "" || data.company.trim() === "" || data.email.trim() === ""}
        >
          {isPending ? "..." : t("signUpSignIn.requestAccess")}
        </Button>

        <div style={{ marginTop: 30, marginBottom: 20 }}>
          {t("signUpSignIn.accessAlreadyGranted")}
          <br />
          <Link to="/users/signup" data-qa-id="RequestAccessPage.signUpLink">
            {t("signUpSignIn.signUpHere")}
          </Link>
        </div>

        <div style={{ marginBottom: 20 }}>
          {t("signUpSignIn.alreadyHaveAccount")}
          <br />
          <Link to="/" data-qa-id="RequestAccessPage.signUpLink">
            {t("signUpSignIn.logInHere")}
          </Link>
        </div>
      </div>
    </LandingLayoutWithLandingDescription>
  );
};
